<script setup lang="ts">
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useOnboardingStore } from '@/stores/onboarding';
import type { DisplayOption } from '@/types/onboarding';
import { Button, Heading, LayoutGap, Prose } from '@dabble/quillui';
import { computed, ref } from 'vue';

const { t } = useI18n();
const onboardingStore = useOnboardingStore();

const selectedGenre = ref<string>(onboardingStore.state.selectedGenres[0] ?? '');

const options = computed<DisplayOption[]>(() => [
  {
    label: t('onboarding.genre.options.romance'),
    image: '/images/genre-romance.svg',
    value: 'romance',
    row: 1,
  },
  {
    label: t('onboarding.genre.options.fantasy'),
    image: '/images/genre-fantasy.svg',
    value: 'fantasy',
    row: 1,
  },
  {
    label: t('onboarding.genre.options.sci_fi'),
    image: '/images/genre-scifi.svg',
    value: 'sci_fi',
    row: 1,
  },
  {
    label: t('onboarding.genre.options.historical'),
    image: '/images/genre-historical.svg',
    value: 'historical',
    row: 2,
  },
  {
    label: t('onboarding.genre.options.mystery'),
    image: '/images/genre-mystery.svg',
    value: 'mystery',
    row: 2,
  },
  {
    label: t('onboarding.genre.options.horror'),
    image: '/images/genre-horror.svg',
    value: 'horror',
    row: 2,
  },
  {
    label: t('onboarding.genre.options.biography'),
    image: '/images/genre-biography.svg',
    value: 'biography',
    row: 3,
  },
  {
    label: t('onboarding.genre.options.general_fiction'),
    image: '/images/genre-general-fiction.svg',
    value: 'general_fiction',
    row: 3,
  },
  {
    label: t('onboarding.genre.options.other'),
    image: '/images/genre-other.svg',
    value: 'other',
    row: 3,
  },
]);

const emit = defineEmits<{
  (e: 'next'): void;
  (e: 'back'): void;
}>();

const handleSelect = (genre: string) => {
  try {
    selectedGenre.value = genre;
    onboardingStore.setSelectedGenres([genre]);
    onboardingStore.reportSurvey({ genre });
    emit('next');
  } catch (error) {
    logError('Failed to save genres:', error);
  }
};

const handlePrevious = () => {
  emit('back');
};
</script>

<template>
  <LayoutGap direction="column" align="center" vertical-align="center" size="medium" fill class="genre-step">
    <Heading :level="1">{{ t('onboarding.genre.title') }}</Heading>
    <Prose variant="secondary" class="genre-step__description">
      {{ t('onboarding.genre.description') }}
    </Prose>

    <LayoutGap direction="row" wrap align="center" size="small" class="genre-step__grid">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="genre-step__tile"
        :class="{ active: selectedGenre === option.value }"
        :aria-pressed="selectedGenre === option.value"
        :aria-label="option.label"
        :disabled="onboardingStore.state.isLoading"
        @click="handleSelect(option.value)"
      >
        <LayoutGap
          direction="column"
          align="center"
          vertical-align="center"
          size="xxsmall"
          class="genre-step__tile-inner"
        >
          <img :src="option.image" alt="" class="genre-step__icon" />
          <span class="genre-step__label">{{ option.label }}</span>
        </LayoutGap>
      </button>
    </LayoutGap>

    <LayoutGap direction="row" align="left" vertical-align="center" fill size="small" class="genre-step__actions">
      <Button
        type="button"
        variant="secondary"
        finish="ghost"
        size="large"
        icon-left="chevron-left"
        :disabled="onboardingStore.state.isLoading"
        @click="handlePrevious"
      >
        {{ t('onboarding.genre.previous') }}
      </Button>
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.genre-step {
  width: 100%;
  max-width: 800px;
  margin-inline: auto;
  text-align: center;

  &__grid {
    margin-bottom: space('large');
  }

  &__tile {
    box-sizing: border-box;
    width: 200px;
    min-height: 130px;
    border: 1px solid theme('color-border-primary');
    border-radius: borderRadius('small');
    background: transparent;
    color: theme('color-text-primary');
    padding: space('small');
    text-transform: uppercase;
    font-size: fontSize('small');
    font-weight: fontWeight('semibold');
    letter-spacing: 0.04em;
    transition:
      transform 0.2s ease,
      border-color 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease;

    &:not(:disabled):hover:not(.active) {
      transform: translateY(-1px);
      border-color: theme('color-border-interactive');
      background-color: theme('color-bg-app-overlay');
    }

    &.active {
      border-color: theme('color-border-focus');
      background-color: theme('color-bg-app-panel');
      box-shadow: elevation('small');
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__tile-inner {
    width: 100%;
    text-align: center;
    pointer-events: none;
  }

  &__icon {
    width: 88px;
    height: 88px;
    object-fit: contain;
  }

  &__label {
    line-height: lineHeight('compact');
    max-width: 100%;
  }
}
</style>
